import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
    AggregateItem,
    TimeSeriesUnits,
} from "~/modules/token_timeseries/types";
import { getRandomColor } from "~/utils/getRandomColor";
import { hexToRGB } from "~/utils/hexToRGB";
import {
    DEFAULT_PIE_CHART_TOOLTIP_PROPS,
    DEFAULT_PIE_PLOT_OPTIONS,
} from "~/modules/visualization/components/PieChart/consts";

// // // //

// ENHANCEMENT - let's make an updated formatCurrencyValue() function that will accept:
// - value
// - smallValueDecimalPlaces
// - maxDenomination (e.g. "B", "M", "K", etc.)
// - compact (boolean) - whether to return a compact format (e.g. "1.2B" instead of "1,200,000,000")
function formatCurrencyValueCompact(value: number): string {
    // Handle trillion or greater
    if (value >= 1_000_000_000_000) {
        return `${(value / 1_000_000_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}T`;
    }

    // Handle billion or greater
    if (value >= 1_000_000_000) {
        return `${(value / 1_000_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}B`;
    }
    // Handle million or greater but less than a billion
    else if (value >= 1_000_000) {
        return `${(value / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}M`;
    }
    // Handle 10k or greater but less than a million
    else if (value >= 10_000) {
        return `${(value / 1000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}K`;
    }

    return value.toLocaleString("en-US", {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
}

// // // //

/** Placement of the legend relative to the plot area. */
export type PieChartLegendPosition = "top" | "right" | "bottom" | "left";

/**
 * Maps a legend position to the Highcharts legend alignment/layout options.
 */
const LEGEND_LAYOUT: Record<
    PieChartLegendPosition,
    { align: string; verticalAlign: string; layout: string }
> = {
    top: { align: "center", verticalAlign: "top", layout: "horizontal" },
    bottom: { align: "center", verticalAlign: "bottom", layout: "horizontal" },
    left: { align: "left", verticalAlign: "middle", layout: "vertical" },
    right: { align: "right", verticalAlign: "middle", layout: "vertical" },
};

interface TokenAggregatesPieChartProps {
    data: AggregateItem[];
    /** Render as a donut chart (a pie with a hollow center) instead of a pie. */
    donut?: boolean;
    /** Whether to show the legend. Defaults to hidden. */
    showLegend?: boolean;
    /** Where the legend sits relative to the plot. Defaults to bottom. */
    legendPosition?: PieChartLegendPosition;
    /** The height of the chart. Defaults to 400px. */
    height?: number;
}

export function TokenAggregatesPieChart({
    data,
    donut = false,
    showLegend = false,
    legendPosition = "bottom",
    height = 400,
}: TokenAggregatesPieChartProps) {
    const series = data
        .map((item) => ({
            name: item.group.name,
            value: item.value,
            color: item.group.color || getRandomColor(),
        }))
        .sort((a, b) => (a.value > b.value ? -1 : 1));

    // Bare number string; the tooltip template prepends the "$" itself, so this
    // must NOT include a currency symbol or it renders a double "$$".
    const formatValue = (value: number) => {
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    // Total across all slices, shown in the center of the donut.
    const total = series.reduce((sum, s) => sum + s.value, 0);
    const isDollarUnit = data[0]?.measure?.unit === TimeSeriesUnits.dollar;
    const centerTotalLabel = `${isDollarUnit ? "$" : ""}${formatCurrencyValueCompact(total)}`;

    const options = {
        title: null,
        chart: {
            backgroundColor: "transparent",
            plotShadow: false,
            type: "pie",
            animation: true,
            height,
            events: {
                render: function (this: any) {
                    const chart = this;

                    // Always clear any previously drawn center label first. This
                    // ensures toggling from donut back to pie removes the stale
                    // centered text instead of leaving it behind.
                    if (chart.__centerTotal) {
                        chart.__centerTotal.destroy();
                        chart.__centerTotal = undefined;
                    }

                    if (!donut) return;

                    const pie = chart.series?.[0];
                    if (!pie || !pie.center) return;

                    const x = chart.plotLeft + pie.center[0];
                    const y = chart.plotTop + pie.center[1];

                    const label = chart.renderer
                        .text(centerTotalLabel, x, y)
                        .attr({ align: "center", zIndex: 5 })
                        .css({
                            color: "var(--foreground)",
                            fontSize: "18px",
                            fontWeight: "600",
                        })
                        .add();

                    // Vertically center the text on the donut's middle.
                    const bbox = label.getBBox();
                    label.attr({ y: y + bbox.height / 4 });

                    chart.__centerTotal = label;
                },
            },
        },
        tooltip: {
            ...DEFAULT_PIE_CHART_TOOLTIP_PROPS,
        },
        plotOptions: {
            series: {
                animation: {
                    duration: 1000,
                },
            },
            pie: {
                ...DEFAULT_PIE_PLOT_OPTIONS.dataLabels,
                borderRadius: 2,
                innerSize: donut ? "60%" : 0,
                showInLegend: showLegend,
                point: {
                    events: {
                        // Prevent clicking a legend item from toggling the
                        // corresponding slice's visibility in the chart.
                        legendItemClick: function () {
                            return false;
                        },
                    },
                },
            },
        },
        credits: {
            enabled: false,
        },
        series: [
            {
                data: series.map((s) => ({
                    name: s.name,
                    color: hexToRGB(s.color, 0.6),
                    y: s.value,
                    dataLabelValue: formatCurrencyValueCompact(Number(s.value)),
                    tooltipValue: formatValue(s.value),
                })),
            },
        ],
        legend: {
            enabled: showLegend,
            ...LEGEND_LAYOUT[legendPosition],
            itemWidth: 150,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}
